import { forwardRef } from 'react';
import { css } from '@emotion/react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { cssForVariant } from './button.service';
import { BUTTON_VARIANTS } from './button.constants';

const StyledButton = styled.button(cssForVariant);

const StyledContainer = styled.span`
  align-items: center;
  display: flex;
`;

const StyledChildren = styled.span`
  display: flex;
  margin-left: ${({ icon, labelPlacement }) => (icon && labelPlacement === 'end' ? '5px' : '0')};
  margin-right: ${({ icon, labelPlacement }) => (icon && labelPlacement === 'start' ? '5px' : '0')};
`;

const IconOrTextRequiredErrorMessage = "Props 'icon' and/or 'children' must be specified for Button component.";

const onlyIcon = css`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 0;
  width: 40px;
`;

/**
 * Renders a <Button /> component.
 * @param {object} props
 * @param {object} props.children This is an array of React components.
 * @param {function} props.variant
 * @param {string} props.disabled
 * @param {string} props.tag
 * @param {string} props.icon
 * @param {object} ref This is a React element reference.
 * @returns {React.Component}
 */

export const Button = forwardRef(({
  children,
  variant = BUTTON_VARIANTS.PRIMARY,
  disabled = false,
  labelPlacement = 'end',
  icon,
  ...restButtonProps
}, forwardedRef) => (
  <StyledButton
    ref={forwardedRef}
    variant={variant}
    disabled={disabled}
    css={icon && !children && onlyIcon}
    {...restButtonProps}
  >
    <StyledContainer>
      {icon && labelPlacement === 'end' && (<StyledChildren>{icon}</StyledChildren>)}
      {children && (<StyledChildren icon={icon} labelPlacement={labelPlacement}>{children}</StyledChildren>)}
      {icon && labelPlacement === 'start' && (<StyledChildren>{icon}</StyledChildren>)}
    </StyledContainer>
  </StyledButton>
));

Button.propTypes = {
  variant: PropTypes.oneOf(Object.values(BUTTON_VARIANTS)),
  disabled: PropTypes.bool,
  labelPlacement: PropTypes.oneOf(['start', 'end']),
  icon: (props) => {
    if (!props.icon && !props.children) {
      return new Error(IconOrTextRequiredErrorMessage);
    }
    if (props.icon) {
      PropTypes.checkPropTypes(
        { icon: PropTypes.node },
        { icon: props.icon },
        'prop',
        'Button',
      );
    }
    return null;
  },
  children: (props) => {
    if (!props.icon && !props.children) {
      return new Error(IconOrTextRequiredErrorMessage);
    }
    if (props.children) {
      PropTypes.checkPropTypes(
        {
          children: PropTypes.oneOfType([
            PropTypes.arrayOf(PropTypes.node),
            PropTypes.node,
          ]),
        },
        { children: props.children },
        'prop',
        'Button',
      );
    }
    return null;
  },
};
